// 引入index.css
import './index.css';
import '../../global/MyFonts.css';
import React, {useContext, useEffect, useState} from "react";
import {USER_AGREEMENT_URL, USER_PRIVACY_URL} from "../../common/constants.ts";
import {ThemeContext} from "../../providers/themeContext/theme-context.tsx";
import {useMusicContext} from "../../providers/musicProvider/music-player-context.tsx";
import {useUserContext} from "../../providers/userProvider/user-context.tsx";
import {PageEnum, PageProps} from "../../types/IPage.ts";
import {JudgmentPlatform} from "../../common/utils/checkPlatform.ts";
import {ToastContext} from "../../components/CustomToast/ToastProvider.tsx";
import {GAEvents} from "../../common/utils/GA4/GAEvents.ts";
import useLoading from "../../providers/loadingContext/useLoading.ts";
import {LocalStorageKey} from "../../types/IGameStat.ts";
import {CookieNameEnum} from "../../common/utils/cookieManage.ts";
import {CallJSBridgeInfoType, NativeBridge} from "../../common/utils/native-bridge/nativeBridge.ts";
import {UrlConfig} from "../../common/config/url-config.ts";
import {apiService} from "../../common/utils/http/api-service.ts";
import {LoginType} from "../../common/utils/http/IResponse.ts";

// 登录页面
const LoginPage: React.FC<PageProps> = ({switchPage}) => {
    const {showLoading, hideLoading} = useLoading();
    const {addToast} = useContext(ToastContext);
    const musicManager = useMusicContext();
    // 全局用户信息provider
    const {hasLoginByApp, setHasLoginByApp, setUser} = useUserContext();
    // 主题管理器
    const {imgUrls} = useContext(ThemeContext);
    // 是否统一隐私协议的状态
    const [checkedPrivacy, setCheckedPrivacy] = useState(false);
    // 是否显示警告
    const [showWarning, setShowWarning] = useState(false);

    // 点击check方法
    const handleCheckChange = () => {
        musicManager.playButtonSound();
        setCheckedPrivacy(!checkedPrivacy);
    }

    // 从app登录后获取到数据之后的回调，这里用于调用自己的后端，将数据传递过去，然后设置全局user数据
    const myAppLoginCallback = async (data: CallJSBridgeInfoType) => {
        try {
            showLoading()
            const {token, nickName, uid} = data;
            // addToast({type: "info", message: `获取到的token:${token},nickName:${nickName},uid:${uid}`})
            // 这里先进行登录，可以在没有头像数据的时候进行登录
            const response = await apiService.loginByCGTN({
                cgtnName: nickName,
                cgtnId: uid,
                authorization: `Bearer ${token}`
            });
            if (response.code === 200) {
                const data = response.data;
                // 这里设置isLogin为true之后，在rootPage已经进行监听，会自动跳转页面到home
                setUser((pre) => ({
                    ...pre,
                    ...data,
                    isLogin: true,
                    loginType: LoginType.CGTN,
                }));
                if (data?.token) {
                    setHasLoginByApp(true)
                    localStorage.setItem(LocalStorageKey.TOKEN, data?.token);
                }
            } else {
                addToast({type: "error", message: `Login error:${response.message}`})
            }
        } finally {
            hideLoading()
        }
    }

    // app登录
    const appLogin = () => {
        if (hasLoginByApp) {
            return;
        }
        try {
            showLoading()
            // 判断平台
            const platform = JudgmentPlatform.getPlatform();

            // 如果是CGTN的ios端原生app上打开
            if (platform === "IOS" || platform === "IOS_dark") {
                NativeBridge.callJSBridgeLoginInfoForIos(myAppLoginCallback);
                setHasLoginByApp(true)
                return;
            }

            // 如果是CGTN的android原生app上打开
            if (platform === "Android" || platform === "Android_dark") {
                NativeBridge.callJSBridgeLoginInfoForAndroid(myAppLoginCallback);
                setHasLoginByApp(true)
                return;
            }
        } catch (e) {
            addToast({type: "error", message: `Oops!Login failed.${e}`})
        } finally {
            hideLoading()
        }
    }

    // 在启动时，就检测是否有CGTN的token，有就直接登录
    useEffect(() => {
        // 发送GA事件
        GAEvents.loginPageShow();
        // 开局判断app登录
        appLogin();
        const handleVisibilityChange = () => {
            // 如果页面显示
            if (!document.hidden) {
                // 页面从隐藏到显示时也判断，主要是用于CGTN登录回调
                appLogin();
            }
        };

        // 如果是web页面登录
        getCGTNUserInfo();

        // 监听页面的显示或者隐藏事件
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // 获取本地cookie信息
    const getAccessToken = () => {
        // 获取所有的 cookie
        const cookies = document.cookie;
        // 查找 access_token 的值
        const accessTokenValue = cookies.match('(^|;)\\s*' + CookieNameEnum.ACCESS_TOKEN + '\\s*=\\s*([^;]+)');
        // 如果找到了 access_token，则返回它的值，否则返回空字符串
        return accessTokenValue ? accessTokenValue[0].split('=')[1].trim() : '';
    }
    const getAccessPassportHost = () => {
        // 获取所有的 cookie
        const cookies = document.cookie;
        // 查找 access_token 的值
        const accessTokenValue = cookies.match('(^|;)\\s*' + CookieNameEnum.PASSPORT_HOST + '\\s*=\\s*([^;]+)');
        // 如果找到了 access_token，则返回它的值，否则返回空字符串
        return accessTokenValue ? accessTokenValue[0].split('=')[1].trim() : '';
    }

    // 根据获取到的token请求官方接口获取用户信息
    const getCGTNUserInfo = async () => {
        if (hasLoginByApp) {
            return;
        }
        try {
            showLoading();
            const accessToken = getAccessToken();
            const passPort = getAccessPassportHost()
            if (accessToken && passPort) {
                // 根据获取到的token取获取用户信息
                const response = await fetch(`${passPort}/service/api/v1/userInfo`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Origin': 'https://www.cgtn.com'
                    }
                });
                if (response.ok) {
                    const responseJson = await response.json();

                    // 处理状态码
                    const statusCodeStr = responseJson['code'].toString();
                    const statusCode = parseInt(statusCodeStr, 10);

                    if (statusCode === 401) {
                        // 授权错误
                        addToast({type: "error", message: "Authorization error "})
                        console.error('Authorization error');
                    } else if (statusCode === 200) {
                        const cgtnId = responseJson['data']['id'] as string;
                        const nickName = responseJson['data']['nick_name'] as string;
                        // const phone = responseJson['data']['phone'] as string;

                        // 调用自己的后台进行登录，换取token
                        const response = await apiService.loginByCGTN({
                            cgtnName: nickName,
                            cgtnId: cgtnId,
                            authorization: `Bearer ${accessToken}`
                        });
                        if (response.code === 200) {
                            const data = response.data;
                            // 这里设置isLogin为true之后，在rootPage已经进行监听，会自动跳转页面到home
                            setUser((pre) => ({
                                ...pre,
                                ...data,
                                isLogin: true,
                                loginType: LoginType.CGTN,
                            }));
                            if (data?.token) {
                                localStorage.setItem(LocalStorageKey.TOKEN, data?.token);
                            }
                        }
                    } else {
                        addToast({type: "error", message: "Oops! Login failed. "})
                    }
                } else {
                    addToast({type: "error", message: "Oops! Login failed. "})
                    console.error(`Request failed with status: ${response.status}`);
                }
            }
        } finally {
            hideLoading()
        }
    }

    // 跳转CGTN登录页面
    const loginByCGTN = async () => {
        setHasLoginByApp(false)
        // 直接跳转不用判断隐私协议
        musicManager.playButtonSound();
        // GA埋点
        GAEvents.loginButtonClick("Login")

        // 判断平台
        const platform = JudgmentPlatform.getPlatform();

        // 如果是CGTN的ios端原生app上打开
        if (platform === "IOS" || platform === "IOS_dark") {
            NativeBridge.loginInIos(myAppLoginCallback);
            return;
        }

        // 如果是CGTN的android原生app上打开
        if (platform === "Android" || platform === "Android_dark") {
            NativeBridge.loginInAndroid(myAppLoginCallback);
            return;
        }
        // 如果是普通页面访问
        location.href = (UrlConfig.CGTN_LOGIN_URL + UrlConfig.CALL_BACK_URL)
    }

    // 游客登录
    const loginByGuest = async () => {
        // 要先统一隐私协议
        if (checkedPrivacy) {
            try {
                showLoading();
                // 调用登录接口
                const response = await apiService.loginByGuest();
                if (response) {
                    // 如果有数据
                    if (response.data) {
                        const userInfo = {
                            ...response.data,
                            loginType: LoginType.GUEST,
                            isLogin: true,
                        }
                        // 设置全局用户信息
                        setUser((pre) => ({
                            ...pre,
                            ...userInfo,
                        }));
                        // 将token保存到本地
                        if (response.data?.token) {
                            localStorage.setItem(LocalStorageKey.TOKEN, response.data?.token);
                        }
                    }
                }
            } catch (error) {
                addToast({message: `Login failed:${error?.toString()}`, type: "error"});
                return;
            } finally {
                hideLoading();
            }

            // 发送GA事件
            GAEvents.loginButtonClick("Guest")
            // 播放按键音
            musicManager.playButtonSound();
            // 切换页面到home
            switchPage(PageEnum.HOME)
        } else {
            // 播放按键音
            musicManager.playButtonSound();
            // 显示隐私提示信息
            setShowWarning(true);
        }
    }

    return (
        <div draggable={false}>
            {/*叠加的背景图*/}
            <div className="login-background-color"/>
            <div className="common-page-background-img" style={{backgroundImage: `url(${imgUrls.rectangleIcon})`}}/>
            <div className="login-background-color-white"/>
            {/*标题*/}
            {/*中间word arena图片*/}
            <div
                className="display-flex-column-common display-flex-justify-content-center display-flex-align-items-center">
                <img className="center-word-arena-logo" src={imgUrls.logoImgUrl} alt="pic"/>
                <img className="center-word-text-pic" src={imgUrls.logoTextImgUrl} alt="pic"/>
                {/*登录按钮*/}
                <div onClick={loginByCGTN} className="login-button-container">
                    <div className="my-roboto-medium text">
                        Log in
                    </div>
                </div>
                {/*分隔线*/}
                <div className="center-line-container">
                    <div className="line"/>
                    <div className="my-roboto-medium text">or</div>
                    <div className="line"/>
                </div>
                {/*游客登录*/}
                <div className="guest-container" onClick={loginByGuest}>
                    <img className="default-avatar-login" src={imgUrls.defaultAvatarUrl} alt="avatar"/>
                    <div className="my-roboto-regular guest-text">Guest</div>
                </div>
                {/*隐私协议*/}
                <div className="privacy-container">
                    <div className="checked-container-outside"  onClick={handleCheckChange}>
                    <div
                        className={`checked-container ${checkedPrivacy ? "checked-container-transparent-border" : "unchecked-container-gray-border"}`}
                        style={{backgroundImage: `url(${checkedPrivacy ? imgUrls.privacyCheckedIconUrl : imgUrls.privacyUnCheckedIconUrl})`}}
                        onClick={handleCheckChange}/>
                    </div>
                    <div className="privacy-text-container">
                        <div className="protocol-container">
                            <div className="my-roboto-regular privacy-text">I agree that l have read and accepted the
                            </div>
                            <a href={USER_PRIVACY_URL}
                               className="my-roboto-regular protocol-text margin-left">Terms</a>
                        </div>
                        <div className="protocol-container">
                            <a href={USER_AGREEMENT_URL} className="my-roboto-regular protocol-text">of Use</a>
                            <div className="my-roboto-regular privacy-text or-text">and</div>
                            <a href={USER_PRIVACY_URL} className="my-roboto-regular protocol-text">Privacy Policy</a>
                            <div className="my-roboto-regular privacy-text">.</div>
                        </div>
                    </div>
                </div>
                {/*未同意隐私协议警告*/}
                {showWarning ? <div className="privacy-warn-container">
                    <div className="warn-text my-roboto-medium">
                        <div>Please click agree to the Terms of Use and</div>
                        <div>acknowledge the Privacy Policy.</div>
                    </div>
                </div> : undefined}
            </div>
        </div>
    );
};

export default LoginPage;
